import type { RouteObject } from 'react-router-dom';
import { cashRoutes } from './cash/routes';
import { salaryRoutes } from './salary/routes';
import { profileRoutes } from './profile/routes';
import { checklistsRoutes } from './checklists/routes';
import { myPayRoutes } from './my-pay/routes';
import { trainingRoutes } from './training/routes';
import { inventoryV2Routes } from './inventory-v2/routes';
import { cafeMenuRoutes } from './cafe-menu/routes';
import { recipeRoutes } from './recipes/routes';
import { salarySubNavItems } from './salary/nav';

export type Role = 'Owner' | 'Admin' | 'Manager' | 'Employee';

export interface SubNavItem {
  to: string;
  label: string;
  end?: boolean;
}

export interface PortalModule {
  key: string;
  label: string;
  icon: string;
  basePath: string;
  allowedRoles: Role[];
  routes: RouteObject[];
  // Optional in-module tabs. Rendered as a segmented control at the top of
  // the content area — never in the bottom tab bar, which is reserved for
  // destinations (Apple's tab bar model).
  subNav?: SubNavItem[];
  // Lower = more prominent. The bottom tab bar takes the four
  // highest-priority modules the current role can see; the rest move into
  // the More drawer. Ordered by how often staff actually open each one.
  tabOrder?: number;
}

export const modules: PortalModule[] = [
  { key: 'help', label: 'Help', icon: '?', basePath: '/help', allowedRoles: ['Owner', 'Admin', 'Manager', 'Employee'], routes: [], tabOrder: 99 },
  { key: 'today', label: 'Today', icon: '⌂', basePath: '/today', allowedRoles: ['Owner', 'Admin', 'Manager', 'Employee'], routes: [], tabOrder: 0 },
  {
    key: 'cash',
    tabOrder: 1,
    label: 'Cash',
    icon: '💵',
    basePath: '/cash',
    allowedRoles: ['Owner', 'Admin', 'Manager', 'Employee'],
    routes: cashRoutes,
  },
  {
    key: 'salary',
    tabOrder: 3,
    label: 'Payroll',
    icon: '💰',
    basePath: '/salary',
    allowedRoles: ['Owner'],
    routes: salaryRoutes,
    subNav: salarySubNavItems,
  },
  {
    key: 'users',
    tabOrder: 9,
    label: 'People',
    icon: '👥',
    basePath: '/users',
    allowedRoles: ['Owner', 'Admin', 'Manager'],
    routes: [],
  },
  {
    key: 'my-pay',
    tabOrder: 5,
    label: 'My Pay',
    icon: '💸',
    basePath: '/my-pay',
    allowedRoles: ['Owner', 'Admin', 'Manager', 'Employee'],
    routes: myPayRoutes,
  },
  {
    key: 'checklists',
    tabOrder: 2,
    label: 'Checklists',
    icon: '✅',
    basePath: '/checklists',
    allowedRoles: ['Owner', 'Admin', 'Manager', 'Employee'],
    routes: checklistsRoutes,
  },
  {
    key: 'inventory',
    tabOrder: 4,
    label: 'Inventory',
    icon: '📦',
    basePath: '/inventory',
    allowedRoles: ['Owner', 'Admin', 'Manager'],
    routes: inventoryV2Routes,
  },
  {
    key: 'cafe-menu',
    tabOrder: 7,
    label: 'Cafe Menu',
    icon: '🍴',
    basePath: '/cafe-menu',
    allowedRoles: ['Owner'],
    routes: cafeMenuRoutes,
  },
  {
    key: 'recipes',
    tabOrder: 6,
    label: 'Recipes',
    icon: '🍽️',
    basePath: '/recipes',
    allowedRoles: ['Owner', 'Admin', 'Manager', 'Employee'],
    routes: recipeRoutes,
  },
  {
    key: 'training',
    tabOrder: 8,
    label: 'Training',
    icon: '📚',
    basePath: '/training',
    allowedRoles: ['Owner', 'Admin', 'Manager', 'Employee'],
    routes: trainingRoutes,
  },
  {
    key: 'profile',
    tabOrder: 10,
    label: 'My Profile',
    icon: '👤',
    basePath: '/profile',
    allowedRoles: ['Owner', 'Admin', 'Manager', 'Employee'],
    routes: profileRoutes,
  },
];

export const getModulesForRole = (role: string): PortalModule[] =>
  modules.filter((m) => (m.allowedRoles as string[]).includes(role)).map(m => ({ ...m, tabOrder: (role === 'Owner' ? ['today', 'cash', 'salary', 'inventory'] : role === 'Employee' ? ['today', 'checklists', 'recipes', 'my-pay'] : ['today', 'cash', 'checklists', 'users']).indexOf(m.key) < 0 ? 99 : (role === 'Owner' ? ['today', 'cash', 'salary', 'inventory'] : role === 'Employee' ? ['today', 'checklists', 'recipes', 'my-pay'] : ['today', 'cash', 'checklists', 'users']).indexOf(m.key) }));

/** Max destinations in the bottom tab bar before the rest move to More. */
export const MAX_TABS = 5;

/**
 * Split a role's modules into bottom-bar tabs and drawer overflow.
 * If everything fits, there is no More tab at all.
 */
export const splitModulesForTabs = (
  visible: PortalModule[]
): { tabs: PortalModule[]; overflow: PortalModule[] } => {
  const ranked = [...visible].sort(
    (a, b) => (a.tabOrder ?? 99) - (b.tabOrder ?? 99)
  );
  if (ranked.length <= MAX_TABS) return { tabs: ranked, overflow: [] };
  return { tabs: ranked.slice(0, MAX_TABS - 1), overflow: ranked.slice(MAX_TABS - 1) };
};

export const getFirstAllowedPath = (role: string): string => {
  const allowed = getModulesForRole(role);
  return allowed.length > 0 ? '/today' : '/login';
};
